import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import LogoPrefeitura from "../../assets/images/logo-prefeitura.png";
import { loginPrefeitura } from "../../services/auth";
import { useAuth } from "../../contexts/AuthContext";
import { recuperarSenhaPrefeitura } from "../../services/prefeitura/gestaoPrefeitura";

interface LoginForm {
  email: string;
  senha: string;
}

const LoginPrefeitura: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<LoginForm>({
    email: "",
    senha: "",
  });

  // Only clear authentication data if explicitly coming from a logout
  useEffect(() => {
    if (isLoading) return;

    const urlParams = new URLSearchParams(window.location.search);
    const shouldClearAuth = urlParams.get("logout") === "true";

    if (shouldClearAuth) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("refreshToken");

      localStorage.removeItem("prefeituraId");
      localStorage.removeItem("prefeituraNome");
      localStorage.removeItem("prefeituraEmail");
      sessionStorage.removeItem("prefeituraId");
      sessionStorage.removeItem("prefeituraNome");
      sessionStorage.removeItem("prefeituraEmail");

      window.history.replaceState({}, "", window.location.pathname);
    } else {
      console.log("[LoginPrefeitura] Preservando tokens existentes...");
    }
  }, [isLoading]);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const userType = localStorage.getItem("userType");
      if (userType === "prefeitura") {
        navigate("/dashboard-prefeitura");
      }
    }
  }, [isLoading, isAuthenticated, navigate]);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recoveryModal, setRecoveryModal] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState("");

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      email: e.target.value,
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      senha: e.target.value,
    }));
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.email) {
      setError("Por favor, digite seu email.");
      return;
    }

    if (!validateEmail(formData.email)) {
      setError("Por favor, digite um email válido.");
      return;
    }

    if (!formData.senha) {
      setError("Por favor, digite sua senha.");
      return;
    }

    setLoading(true);

    try {
      const response = await loginPrefeitura(formData);

      if (response.data) {
        const loginData = response.data;

        // Salvar dados no localStorage
        localStorage.setItem("token", loginData.token);
        localStorage.setItem("userType", "prefeitura");
        localStorage.setItem(
          "userId",
          loginData.prefeituraId?.toString() || "",
        );
        localStorage.setItem("userName", loginData.nomePrefeitura || "");
        localStorage.setItem("userEmail", loginData.email || "");

        // Salvar todos os dados da prefeitura
        localStorage.setItem(
          "prefeituraData",
          JSON.stringify({
            id: loginData.prefeituraId,
            nome: loginData.nomePrefeitura,
            email: loginData.email,
            telefone: loginData.telefone || "",
            whatsapp: loginData.whatsapp || "",
          }),
        );
        
        // Salvar dados individuais para fácil acesso
        localStorage.setItem("prefeituraId", loginData.prefeituraId?.toString() || "");
        localStorage.setItem("prefeituraNome", loginData.nomePrefeitura || "");
        localStorage.setItem("prefeituraEmail", loginData.email || "");
        localStorage.setItem("prefeituraTelefone", loginData.telefone || "");
        localStorage.setItem("prefeituraWhatsapp", loginData.whatsapp || "");

        const userData = {
          id: parseInt(loginData.prefeituraId?.toString() || "0"),
          nome: loginData.nomePrefeitura || "",
          email: loginData.email || "",
          tipo: "prefeitura" as const,
        };

        login(loginData.token, userData);

        setLoading(false);

        setTimeout(() => {
          navigate("/dashboard-prefeitura");
        }, 500);
      }
    } catch (error: any) {
      setLoading(false);

      // Tratar diferentes tipos de erro
      if (error.code === "ERR_NETWORK") {
        setError(
          "Erro de conexão: Backend não está acessível. Verifique se o servidor está rodando na porta 8093.",
        );
      } else if (error.response) {
        // Erro da API
        if (error.response.status === 401) {
          setError("Email ou senha incorretos. Verifique suas credenciais.");
        } else if (error.response.status === 403) {
          setError(
            error.response.data?.message ||
              "Prefeitura inativa. Entre em contato com o suporte.",
          );
        } else if (error.response.status === 404) {
          setError("Prefeitura não encontrada. Verifique o email digitado.");
        } else if (error.response.status === 500) {
          setError("Erro no servidor. Tente novamente mais tarde.");
        } else {
          setError(
            error.response.data?.message ||
              "Erro ao fazer login. Tente novamente.",
          );
        }
      } else if (error.request) {
        // Requisição feita mas sem resposta
        setError(
          "Sem resposta do servidor. Verifique sua conexão e se o backend está rodando.",
        );
      } else {
        // Outro tipo de erro
        setError(error.message || "Erro inesperado. Tente novamente.");
      }
    }
  };

  const handleRecoveryCancel = () => {
    setRecoveryModal(false);
    setRecoveryEmail("");
    setRecoveryMessage("");
  };

  const handleRecoverySubmit = async () => {
    if (!recoveryEmail) {
      setRecoveryMessage("Por favor, digite um email.");
      return;
    }

    if (!validateEmail(recoveryEmail)) {
      setRecoveryMessage("Por favor, digite um email válido.");
      return;
    }

    setRecoveryLoading(true);
    setRecoveryMessage("");

    try {
      const response = await recuperarSenhaPrefeitura(recoveryEmail);

      if (response.success) {
        setRecoveryMessage(
          "Instruções de recuperação enviadas para o email informado."
        );
        setTimeout(() => {
          handleRecoveryCancel();
        }, 3000);
      } else {
        setRecoveryMessage(
          response.message || "Erro ao enviar email de recuperação. Tente novamente."
        );
      }
    } catch (error) {
      setRecoveryMessage(
        "Erro ao conectar com o servidor. Tente novamente mais tarde."
      );
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 5,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 2,
            backgroundColor: "#ffffff",
            maxWidth: 450,
            margin: "0 auto",
            minHeight: 650,
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={LogoPrefeitura}
            alt="Logo Prefeitura"
            sx={{
              width: 240,
              height: "auto",
              mb: 3,
              objectFit: "contain",
            }}
          />

          {/* Título */}
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, mb: 0.5, color: "#333", textAlign: "center" }}
          >
            Acesso Administrativo
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
            Prefeitura de Bom Retiro do Sul
          </Typography>

          {/* Formulário */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              mt: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleEmailChange}
              margin="normal"
              required
              placeholder="email@prefeitura.rs.gov.br"
              variant="outlined"
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderWidth: 1,
                  },
                  "&:hover fieldset": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "0.875rem",
                  "&.Mui-focused": {
                    color: "#667eea",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "16px 14px",
                  color: "#333",
                  "&::placeholder": {
                    color: "#999",
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: "#666", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Senha"
              type={showPassword ? "text" : "password"}
              value={formData.senha}
              onChange={handlePasswordChange}
              margin="normal"
              required
              variant="outlined"
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderWidth: 1,
                  },
                  "&:hover fieldset": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "0.875rem",
                  "&.Mui-focused": {
                    color: "#667eea",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "16px 14px",
                  color: "#333",
                  "&::placeholder": {
                    color: "#999",
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: "#666", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: "#666" }}
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={!loading && <AccountBalanceIcon />}
              sx={{
                mt: 1,
                mb: 2,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: "#667eea",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#5a67d8",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Acessar Painel Administrativo"
              )}
            </Button>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setRecoveryModal(true)}
                sx={{
                  textTransform: "none",
                  color: "#666",
                  fontWeight: 400,
                  fontSize: "0.875rem",
                  mb: 2,
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                    color: "#667eea",
                  },
                }}
              >
                Esqueceu sua senha?
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Modal de Recuperação de Senha */}
      <Dialog
        open={recoveryModal}
        onClose={handleRecoveryCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1, backgroundColor: "#ffffff" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
            Recuperar Senha
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2, backgroundColor: "#ffffff" }}>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: "#666", textAlign: "center" }}
          >
            Digite o email cadastrado da prefeitura para receber as instruções
            de recuperação de senha.
          </Typography>

          {recoveryMessage && (
            <Alert
              severity={
                recoveryMessage.includes("enviadas") ? "success" : "error"
              }
              sx={{ mb: 2 }}
            >
              {recoveryMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email institucional"
            type="email"
            value={recoveryEmail}
            onChange={(e) => setRecoveryEmail(e.target.value)}
            variant="outlined"
            disabled={recoveryLoading}
            placeholder="email@prefeitura.rs.gov.br"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#ffffff",
                borderRadius: 2,
                "& fieldset": {
                  borderColor: "#e0e0e0",
                  borderWidth: 1,
                },
                "&:hover fieldset": {
                  borderColor: "#bdbdbd",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#667eea",
                  borderWidth: 2,
                },
              },
              "& .MuiInputLabel-root": {
                color: "#666",
                "&.Mui-focused": {
                  color: "#667eea",
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "16px 14px",
                color: "#333",
                "&::placeholder": {
                  color: "#999",
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: "#666", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1, backgroundColor: "#ffffff" }}>
          <Button
            onClick={handleRecoveryCancel}
            variant="outlined"
            disabled={recoveryLoading}
            sx={{
              textTransform: "none",
              color: "#666",
              borderColor: "#e0e0e0",
              "&:hover": {
                borderColor: "#bdbdbd",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRecoverySubmit}
            variant="contained"
            disabled={recoveryLoading || !recoveryEmail}
            sx={{
              textTransform: "none",
              backgroundColor: "#667eea",
              "&:hover": {
                backgroundColor: "#5a67d8",
              },
            }}
          >
            {recoveryLoading ? <CircularProgress size={24} color="inherit" /> : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPrefeitura;
